import { fireBeacon } from './beacon.js';
import { renderCompanion } from './companion.js';
import { selectMedia } from './vast.js';
import type { ParsedAd, PlayerHandle, PlayerOptions, VastEvent } from './types.js';

const STRINGS = {
  en: {
    skipIn: (s: number) => `Skip in ${s}s`,
    skipNow: 'Skip ad',
    adLabel: 'Ad',
    errorFallback: 'Ad unavailable',
  },
  ar: {
    skipIn: (s: number) => `تخطّي بعد ${s} ث`,
    skipNow: 'تخطّي الإعلان',
    adLabel: 'إعلان',
    errorFallback: 'الإعلان غير متاح',
  },
};

/**
 * Play a parsed VAST InLine. Single ad — caller orchestrates AdPod
 * sequencing. Promise resolves when the ad reaches `complete` or `skip`,
 * rejects on hard error (no playable media file, video element fails).
 */
export function playAd(ad: ParsedAd, opts: PlayerOptions): PlayerHandle & { done: Promise<void> } {
  const t = STRINGS[opts.locale ?? 'en'];
  const skipAfter = opts.skipAfter ?? 5;
  // Outstream players start paused — IntersectionObserver kicks playback
  // off only once the slot is sufficiently visible. Instream keeps the
  // existing autoplay semantics.
  const outstream = opts.outstream === true;
  const autoplay = outstream ? false : (opts.autoplay ?? true);
  const muted = opts.muted ?? true;
  const controls = opts.controls ?? true;
  const viewabilityThreshold = clampThreshold(opts.viewabilityThreshold ?? 0.5);
  const collapseOnComplete = opts.collapseOnComplete ?? outstream;

  const wrap = document.createElement('div');
  wrap.className = 'mpwa-vp';
  wrap.style.cssText = 'position:relative;width:100%;background:#000;line-height:0;font-family:system-ui,-apple-system,sans-serif;';

  const video = document.createElement('video');
  video.style.cssText = 'width:100%;height:auto;display:block;background:#000;';
  video.playsInline = true;
  if (autoplay) video.autoplay = true;
  if (muted) video.muted = true;
  if (controls) video.controls = true;
  video.preload = 'auto';

  // Pick media. Fallback to MP4 anyway; HLS handled in caller (dynamic import).
  const containerW = opts.container.clientWidth || 1280;
  const picked = selectMedia(ad.mediaFiles, { maxWidth: containerW * 2 });
  if (!picked) {
    const err = new Error('no playable MediaFile');
    opts.onError?.(err);
    return rejectedHandle(err);
  }
  ad.mediaUrl = picked.url;
  video.src = picked.url;

  // Ad label badge (top-left).
  const badge = document.createElement('span');
  badge.textContent = t.adLabel;
  badge.style.cssText = 'position:absolute;top:8px;inset-inline-start:8px;background:rgba(0,0,0,.65);color:#fff;font-size:11px;padding:2px 8px;border-radius:3px;letter-spacing:.5px;z-index:2;pointer-events:none;';

  // Click overlay (open click-through, fire VideoClicks/ClickTracking).
  const clickOverlay = document.createElement('a');
  clickOverlay.style.cssText = 'position:absolute;inset:0;display:block;z-index:1;';
  if (ad.clickThrough) {
    clickOverlay.href = ad.clickThrough;
    clickOverlay.target = '_blank';
    clickOverlay.rel = 'noopener noreferrer sponsored';
    clickOverlay.addEventListener('click', () => {
      ad.clickTracking.forEach(fireBeacon);
    });
  } else {
    clickOverlay.style.cursor = 'default';
  }

  // Skip button (bottom-right). Hidden until skipAfter elapses.
  const skip = document.createElement('button');
  skip.type = 'button';
  skip.style.cssText = 'position:absolute;bottom:8px;inset-inline-end:8px;background:rgba(0,0,0,.7);color:#fff;border:1px solid rgba(255,255,255,.3);padding:6px 12px;font-size:12px;font-weight:500;border-radius:3px;cursor:pointer;z-index:3;backdrop-filter:blur(4px);';
  skip.textContent = t.skipIn(skipAfter);
  skip.disabled = true;
  if (skipAfter <= 0) skip.style.display = 'none';

  wrap.appendChild(video);
  wrap.appendChild(clickOverlay);
  wrap.appendChild(badge);
  if (skipAfter > 0) wrap.appendChild(skip);
  opts.container.appendChild(wrap);

  // ---- tracking ----
  const fired = new Set<VastEvent>();
  const trackMap = bucketTracking(ad);
  function emit(event: VastEvent): void {
    if (fired.has(event) && event !== 'progress') return;
    fired.add(event);
    (trackMap.get(event) ?? []).forEach(fireBeacon);
    opts.onEvent?.(event, ad);
  }

  let resolveDone: () => void;
  let rejectDone: (err: Error) => void;
  const done = new Promise<void>((res, rej) => {
    resolveDone = res;
    rejectDone = rej;
  });
  let finished = false;
  function finish(): void {
    if (finished) return;
    finished = true;
    teardown();
    if (collapseOnComplete) {
      wrap.style.display = 'none';
    }
    opts.onAllAdsCompleted?.();
    resolveDone();
  }
  function fail(err: Error): void {
    if (finished) return;
    finished = true;
    teardown();
    opts.onError?.(err);
    rejectDone(err);
  }
  function teardown(): void {
    clearInterval(quartileTimer);
    if (skipTimer) clearTimeout(skipTimer);
    if (skipCountdown) clearInterval(skipCountdown);
    if (viewObserver) {
      viewObserver.disconnect();
      viewObserver = undefined;
    }
    video.removeAttribute('src');
    video.load();
  }

  // Impressions: fire as soon as the video element starts loading.
  ad.impressionUrls.forEach(fireBeacon);

  // ---- quartile tracking ----
  let quartileTimer: ReturnType<typeof setInterval> | undefined;
  function setupQuartiles(durationSec: number): void {
    if (!durationSec || !Number.isFinite(durationSec) || durationSec <= 0) return;
    quartileTimer = setInterval(() => {
      if (!video || video.paused || video.ended) return;
      const pct = video.currentTime / durationSec;
      if (pct >= 0.25 && !fired.has('firstQuartile')) emit('firstQuartile');
      if (pct >= 0.5 && !fired.has('midpoint')) emit('midpoint');
      if (pct >= 0.75 && !fired.has('thirdQuartile')) emit('thirdQuartile');
    }, 250);
  }

  // ---- skip button countdown ----
  let skipTimer: ReturnType<typeof setTimeout> | undefined;
  let skipCountdown: ReturnType<typeof setInterval> | undefined;
  let remaining = skipAfter;
  if (skipAfter > 0) {
    skipCountdown = setInterval(() => {
      remaining = Math.max(0, remaining - 1);
      skip.textContent = remaining > 0 ? t.skipIn(remaining) : t.skipNow;
    }, 1000);
    skipTimer = setTimeout(() => {
      skip.disabled = false;
      skip.style.cursor = 'pointer';
      if (skipCountdown) clearInterval(skipCountdown);
      skip.textContent = t.skipNow;
    }, skipAfter * 1000);
  }
  skip.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (skip.disabled) return;
    emit('skip');
    finish();
  });

  // ---- video element wiring ----
  video.addEventListener('loadedmetadata', () => {
    const dur = ad.durationSec && ad.durationSec > 0 ? ad.durationSec : video.duration;
    setupQuartiles(dur);
  });
  video.addEventListener('play', () => emit('start'));
  video.addEventListener('pause', () => {
    if (!video.ended) emit('pause');
  });
  video.addEventListener('playing', () => {
    if (fired.has('pause')) {
      fired.delete('pause');
      emit('resume');
    }
  });
  video.addEventListener('volumechange', () => {
    emit(video.muted || video.volume === 0 ? 'mute' : 'unmute');
  });
  video.addEventListener('ended', () => {
    emit('complete');
    finish();
  });
  video.addEventListener('error', () => {
    ad.errorUrls.forEach(fireBeacon);
    fail(new Error('video element error'));
  });

  // Render companions.
  if (opts.companionContainer && ad.companions.length > 0) {
    try {
      renderCompanion(opts.companionContainer, ad.companions);
    } catch {
      // companion render is best-effort
    }
  }

  // ---- outstream viewability gating ----
  // When `outstream` is true, gate playback on visible-fraction-of-wrapper
  // crossing the threshold. The video element's native pause/play events
  // already fan out to VAST pause/resume tracking, so we don't double-emit.
  let viewObserver: IntersectionObserver | undefined;
  if (outstream && typeof IntersectionObserver !== 'undefined') {
    let visible = false;
    let playPending = false;
    viewObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const nowVisible = entry.intersectionRatio >= viewabilityThreshold;
        if (nowVisible === visible) continue;
        visible = nowVisible;
        if (visible) {
          // Best-effort play — autoplay policies require muted; we default
          // muted=true in outstream so this normally succeeds.
          playPending = true;
          video.play().catch(() => { /* user gesture required; ignore */ }).finally(() => {
            playPending = false;
          });
        } else if (!playPending && !video.paused) {
          video.pause();
        }
      }
    }, { threshold: [0, viewabilityThreshold, 1] });
    viewObserver.observe(wrap);
  }

  return {
    skip(): void {
      skip.disabled = false;
      skip.click();
    },
    destroy(): void {
      teardown();
      wrap.remove();
    },
    done,
  };
}

function rejectedHandle(err: Error): PlayerHandle & { done: Promise<void> } {
  return {
    skip(): void { /* no-op */ },
    destroy(): void { /* no-op */ },
    done: Promise.reject(err),
  };
}

function clampThreshold(v: number): number {
  if (!Number.isFinite(v)) return 0.5;
  if (v <= 0) return 0.01;
  if (v > 1) return 1;
  return v;
}

function bucketTracking(ad: ParsedAd): Map<VastEvent, string[]> {
  const m = new Map<VastEvent, string[]>();
  for (const t of ad.trackingEvents) {
    const existing = m.get(t.event);
    if (existing) existing.push(t.url);
    else m.set(t.event, [t.url]);
  }
  return m;
}
